import React, { useState, useEffect, useCallback } from 'react';
import { useSetCurrentPage } from '../stores/useAppStore';
import GradientBackground from '../components/ui/GradientBackground';
import ParticleBackground from '../components/ui/ParticleBackground';
import GlassCard from '../components/ui/GlassCard';
import AnimatedTitle from '../components/ui/AnimatedTitle';
import ScrollReveal from '../components/ui/ScrollReveal';
import PageHeader from '../components/PageHeader';
import {
  stockStrategyService,
  StockStrategy,
  StockStrategyResult,
  StockDataStats,
  StockHistorySignal,
  StockSignalPerformanceStats,
} from '../services/stockStrategyService';
import { useToastStore } from '../stores/useToastStore';
import { stockPortfolioService } from '../services/stockPortfolioService';
import { researchReportService, ResearchReport } from '../services/researchReportService';
import ResearchReportModal from '../components/ResearchReports/ResearchReportModal';

const StockStrategies: React.FC = React.memo(() => {
  const setCurrentPage = useSetCurrentPage();
  const showToast = useToastStore((state) => state.show);

  const [strategies, setStrategies] = useState<StockStrategy[]>([]);
  const [loading, setLoading] = useState(true);
  const [executingId, setExecutingId] = useState<string | null>(null);
  const [results, setResults] = useState<StockStrategyResult[]>([]);
  const [selectedStrategy, setSelectedStrategy] = useState<StockStrategy | null>(null);
  const [stats, setStats] = useState<StockDataStats | null>(null);
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [currentReport, setCurrentReport] = useState<ResearchReport | null>(null);
  const [isReportLoading, setIsReportLoading] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);

  /**
   * @DEBUG_ID coreSignalsState
   * @description 核心策略信号状态，用于 IDE 调试和快速定位。
   * 关联数据源: stockStrategyService.getCoreStrategySignals
   */
  const [coreSignals, setCoreSignals] = useState<StockHistorySignal[]>([]);
  const [coreSignalsLoading, setCoreSignalsLoading] = useState(false);
  const [performanceStats, setPerformanceStats] = useState<StockSignalPerformanceStats | null>(null);
  const [showPerformanceModal, setShowPerformanceModal] = useState(false);

  const fetchCoreSignals = useCallback(async () => {
    try {
      setCoreSignalsLoading(true);
      const [data, perfData] = await Promise.all([
        stockStrategyService.getCoreStrategySignals({ limit: 10 }),
        stockStrategyService.getStockSignalPerformanceStats(30),
      ]);
      setCoreSignals(data.signals);
      setPerformanceStats(perfData);
    } catch (error: any) {
      console.error('Fetch core signals error:', error);
    } finally {
      setCoreSignalsLoading(false);
    }
  }, []);

  const fetchStrategies = useCallback(
    async (forceInit = false) => {
      try {
        setLoading(true);
        // 并行获取核心信号
        fetchCoreSignals();

        // 如果是强制初始化或者列表为空，先执行初始化
        if (forceInit) {
          await stockStrategyService.initStrategies();
        }

        // 获取策略列表和统计数据
        const [data, statsData] = await Promise.all([
          stockStrategyService.getAllStrategies(),
          stockStrategyService.getStockDataStats(),
        ]);

        setStats(statsData);
        // 如果有未同步的数据，显示提示
        if (statsData && statsData.unsyncedCount > 0) {
          setShowStatsModal(true);
        }

        // 如果没有策略且不是强制初始化，尝试自动初始化一次
        if (data.length === 0 && !forceInit) {
          await stockStrategyService.initStrategies();
          const refreshed = await stockStrategyService.getAllStrategies();
          setStrategies(refreshed);
        } else {
          setStrategies(data);
        }
      } catch (error: any) {
        showToast(error.message || '获取数据失败', 'error');
      } finally {
        setLoading(false);
      }
    },
    [showToast, fetchCoreSignals],
  );

  useEffect(() => {
    fetchStrategies();
  }, [fetchStrategies]);

  const handleExecute = async (strategy: StockStrategy) => {
    try {
      setExecutingId(strategy.strategyId);
      setSelectedStrategy(strategy);
      const data = await stockStrategyService.executeStrategy(strategy.strategyId);
      console.log('Strategy execution result:', data);
      setResults(data);
      if (data.length > 0) {
        showToast(`策略执行成功，找到 ${data.length} 只符合条件的股票`, 'success');
      } else {
        showToast(`未找到符合条件的股票`, 'info');
      }
    } catch (error: any) {
      showToast(error.message || '执行策略失败', 'error');
    } finally {
      setExecutingId(null);
    }
  };

  const handleShowReport = async (symbol: string) => {
    try {
      setIsReportLoading(true);
      setShowReportModal(true);
      const report = await researchReportService.getResearchReportBySymbol(symbol);
      if (report) {
        setCurrentReport(report);
      } else {
        showToast(`暂无该股票 (${symbol}) 的深度研报`, 'info');
        setShowReportModal(false);
      }
    } catch (error: any) {
      showToast('获取研报失败', 'error');
      setShowReportModal(false);
    } finally {
      setIsReportLoading(false);
    }
  };

  const handleAddToPortfolio = async (stock: StockStrategyResult) => {
    try {
      if (!stock.price) {
        showToast('无法获取当前价格，开仓失败', 'error');
        return;
      }
      await stockPortfolioService.openPosition({
        symbol: stock.symbol,
        name: stock.name,
        entryPrice: stock.price,
        metadata: {
          strategy: selectedStrategy?.name,
          industry: stock.industry,
          change: stock.change,
        },
      });
      showToast(`已成功将 ${stock.name} 加入模拟持仓`, 'success');
    } catch (error: any) {
      showToast(error.message || '加入持仓失败', 'error');
    }
  };

  return (
    <GradientBackground className="pb-20">
      <ParticleBackground />

      <PageHeader
        title="选股策略"
        onBack={() => setCurrentPage('stock-dashboard')}
        rightContent={
          <button
            onClick={() => fetchStrategies(true)}
            className="flex items-center justify-center w-9 h-9 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white hover:bg-white/20 hover:border-white/30 transition-all duration-300 hover:scale-105"
            disabled={loading}
            aria-label="刷新"
            title="同步并刷新策略列表"
          >
            <svg
              className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
          </button>
        }
      />

      <div className="pt-16 px-4 space-y-4 relative z-10">
        {/* 核心策略高置信度信号 */}
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <AnimatedTitle variant="glow" size="sm" className="mb-0">
                🔥 核心策略实时信号
              </AnimatedTitle>
              {performanceStats && (
                <button
                  onClick={() => setShowPerformanceModal(true)}
                  className="px-1.5 py-0.5 bg-green-500/10 text-green-400 text-[9px] rounded border border-green-500/20 flex items-center gap-1"
                >
                  <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></span>
                  胜率 {performanceStats.winRate.toFixed(1)}%
                </button>
              )}
            </div>
            <button
              onClick={fetchCoreSignals}
              className="text-[10px] text-blue-400 hover:text-blue-300 transition-colors"
            >
              刷新信号
            </button>
          </div>

          {coreSignalsLoading && coreSignals.length === 0 ? (
            <div className="flex justify-center py-4">
              <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          ) : coreSignals.length > 0 ? (
            <div className="flex overflow-x-auto pb-2 gap-3 scrollbar-hide no-scrollbar">
              {coreSignals.map((signal) => {
                const confidence = signal.metadata?.confidenceScore || 0;
                const isHighConfidence = confidence >= 85;
                const pnl = signal.pnl1d != null ? signal.pnl1d * 100 : null;

                return (
                  <GlassCard
                    key={signal.id}
                    className={`flex-shrink-0 w-48 p-3 border ${isHighConfidence ? 'border-amber-500/30 bg-amber-500/5' : 'border-white/10'}`}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <div className="flex items-center gap-1.5">
                          <span className="text-white font-bold text-sm">{signal.name}</span>
                          {isHighConfidence && <span className="text-[10px]">💎</span>}
                        </div>
                        <span className="text-white/30 text-[9px] font-mono">{signal.symbol}</span>
                      </div>
                      <div className="text-right">
                        <div
                          className={`text-[10px] font-bold ${isHighConfidence ? 'text-amber-400' : 'text-blue-400'}`}
                        >
                          {confidence}分
                        </div>
                        <div className="text-[8px] text-white/40">置信度</div>
                      </div>
                    </div>

                    <div className="flex justify-between items-center mt-2 pt-2 border-t border-white/5">
                      <div className="text-[10px]">
                        <span className="text-white/40">价格:</span>
                        <span className="text-white font-mono ml-1">{signal.price.toFixed(2)}</span>
                      </div>
                      {pnl !== null && (
                        <div className={`text-[10px] font-bold ${pnl >= 0 ? 'text-red-400' : 'text-green-400'}`}>
                          {pnl >= 0 ? '+' : ''}
                          {pnl.toFixed(2)}%
                        </div>
                      )}
                    </div>

                    <div className="mt-3 flex gap-2">
                      <button
                        onClick={() => handleShowReport(signal.symbol)}
                        className={`flex-1 py-1 rounded text-[9px] font-bold transition-all ${
                          isHighConfidence
                            ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                            : 'bg-white/5 text-white/60 border border-white/10'
                        }`}
                      >
                        {isHighConfidence ? '🤖 AI 研报' : '查看研报'}
                      </button>
                      <button
                        onClick={() =>
                          handleAddToPortfolio({
                            symbol: signal.symbol,
                            name: signal.name,
                            price: signal.price,
                            change: signal.change,
                            amount: 0,
                            turnover: 0,
                            mktcap: 0,
                            industry: signal.industry || '未知',
                          })
                        }
                        className="flex-1 py-1 bg-blue-500/20 text-blue-400 border border-blue-500/30 rounded text-[9px] font-bold transition-all"
                      >
                        开仓
                      </button>
                    </div>
                  </GlassCard>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-4 bg-white/5 rounded-xl border border-white/5">
              <p className="text-white/20 text-[10px]">暂无实时信号</p>
            </div>
          )}
        </div>

        {/* 策略列表 */}
        <div className="space-y-1.5">
          <AnimatedTitle variant="glow" size="sm" className="mb-0">
            可用策略库
          </AnimatedTitle>

          {loading && strategies.length === 0 ? (
            <div className="flex justify-center py-10">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4">
              {strategies.map((strategy, index) => (
                <ScrollReveal key={strategy.id} animation="fadeInUp" delay={100 + index * 50}>
                  <GlassCard
                    className={`p-4 transition-all border ${selectedStrategy?.strategyId === strategy.strategyId ? 'border-blue-500/50 bg-blue-500/5' : 'border-white/10'}`}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="text-white font-bold">{strategy.name}</h3>
                          {strategy.isSystem && (
                            <span className="px-1.5 py-0.5 bg-blue-500/20 text-blue-400 text-[10px] rounded border border-blue-500/30">
                              系统
                            </span>
                          )}
                        </div>
                        <p className="text-white/60 text-xs mt-1">{strategy.description}</p>
                      </div>
                      <button
                        onClick={() => handleExecute(strategy)}
                        disabled={executingId === strategy.strategyId}
                        className={`px-3 py-1.5 rounded-lg text-sm transition-all duration-300 whitespace-nowrap flex items-center justify-center border disabled:opacity-50 disabled:cursor-not-allowed ${
                          executingId === strategy.strategyId
                            ? 'bg-white/5 text-white/20 border-white/10'
                            : 'bg-green-500/20 text-green-300 border-green-500/50 hover:bg-green-500/30'
                        }`}
                      >
                        {executingId === strategy.strategyId ? (
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            <span>运行中</span>
                          </div>
                        ) : (
                          '运行策略'
                        )}
                      </button>
                    </div>

                    <div className="flex flex-wrap gap-2 mt-3">
                      {Object.entries(strategy.conditions).map(
                        ([key, value]) =>
                          value !== undefined &&
                          value !== null && (
                            <span
                              key={key}
                              className="text-[10px] px-2 py-0.5 bg-white/5 text-white/40 rounded-md border border-white/5"
                            >
                              {key}:{' '}
                              {typeof value === 'number' && value > 1000000
                                ? `${(value / 100000000).toFixed(2)}亿`
                                : value}
                            </span>
                          ),
                      )}
                    </div>
                  </GlassCard>
                </ScrollReveal>
              ))}
            </div>
          )}
        </div>

        {/* 运行结果 */}
        {results.length > 0 && selectedStrategy && (
          <div className="space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="flex justify-between items-center">
              <AnimatedTitle variant="neon" size="sm">
                选股结果 ({results.length})
              </AnimatedTitle>
              <button onClick={() => setResults([])} className="text-xs text-white/40 hover:text-white/60">
                清除结果
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {results.map((stock, i) => (
                <GlassCard key={i} className="p-4 hover:border-white/20 transition-all group relative overflow-hidden">
                  {/* 背景装饰 */}
                  <div
                    className={`absolute -right-4 -top-4 w-16 h-16 blur-2xl opacity-20 transition-opacity group-hover:opacity-40 ${(stock.change || 0) >= 0 ? 'bg-red-500' : 'bg-green-500'}`}
                  />

                  <div className="relative z-10">
                    {/* 头部：名称、代码、行业 */}
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="text-white font-bold text-base tracking-tight">{stock.name}</h4>
                          <span className="text-white/30 text-[10px] font-mono bg-white/5 px-1 rounded">
                            {stock.symbol}
                          </span>
                        </div>
                        <p className="text-white/40 text-xs">{stock.industry}</p>
                      </div>
                      <div
                        className={`px-2 py-1 rounded-md font-bold text-sm ${(stock.change || 0) >= 0 ? 'bg-red-500/10 text-red-400' : 'bg-green-500/10 text-green-400'}`}
                      >
                        {stock.change != null ? (
                          <>
                            {(stock.change || 0) >= 0 ? '+' : ''}
                            {stock.change.toFixed(2)}%
                          </>
                        ) : (
                          '--'
                        )}
                      </div>
                    </div>

                    {/* 主体：价格和估值 */}
                    <div className="flex justify-between items-end mb-4">
                      <div>
                        <span className="text-white/40 text-[10px] block mb-0.5">最新价</span>
                        <p className="text-2xl font-mono font-bold text-white leading-none">
                          <span className="text-sm mr-0.5 opacity-60">¥</span>
                          {stock.price != null ? stock.price.toFixed(2) : '--'}
                        </p>
                      </div>
                      <div className="text-right">
                        {stock.peRatio != null && (
                          <div className="flex items-center justify-end gap-1.5 mb-1">
                            <span className="text-white/30 text-[10px]">PE(动)</span>
                            <span className="text-white/70 text-xs font-mono">{stock.peRatio.toFixed(1)}</span>
                          </div>
                        )}
                        {stock.pbRatio != null && (
                          <div className="flex items-center justify-end gap-1.5">
                            <span className="text-white/30 text-[10px]">PB</span>
                            <span className="text-white/70 text-xs font-mono">{stock.pbRatio.toFixed(1)}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* 底部指标网格 */}
                    <div className="grid grid-cols-2 gap-y-2.5 gap-x-4 pt-3 border-t border-white/5">
                      <div>
                        <span className="text-white/30 text-[10px] block">成交额</span>
                        <span className="text-white/80 text-xs font-mono">
                          {stock.amount != null ? `${(stock.amount / 100000000).toFixed(2)}亿` : '--'}
                        </span>
                      </div>
                      <div className="text-right">
                        <span className="text-white/30 text-[10px] block">换手率</span>
                        <span className="text-white/80 text-xs font-mono">
                          {stock.turnover != null ? `${stock.turnover.toFixed(2)}%` : '--'}
                        </span>
                      </div>
                      <div>
                        <span className="text-white/30 text-[10px] block">总市值</span>
                        <span className="text-white/80 text-xs font-mono">
                          {stock.totalMktCap != null
                            ? `${(stock.totalMktCap / 100000000).toFixed(2)}亿`
                            : stock.mktcap != null
                              ? `${(stock.mktcap / 100000000).toFixed(2)}亿`
                              : '--'}
                        </span>
                      </div>
                      <div className="text-right">
                        <span className="text-white/30 text-[10px] block">量比</span>
                        <span className="text-white/80 text-xs font-mono">
                          {stock.volumeRatio != null ? stock.volumeRatio.toFixed(2) : '--'}
                        </span>
                      </div>
                    </div>

                    {/* 操作栏 */}
                    <div className="mt-4 pt-3 border-t border-white/5 flex gap-2">
                      <button
                        onClick={() => handleShowReport(stock.symbol)}
                        className="flex-1 py-1.5 bg-cyan-500/10 hover:bg-cyan-500/20 border border-cyan-500/30 rounded-lg text-cyan-400 text-[10px] font-bold transition-all flex items-center justify-center gap-1.5"
                      >
                        <span>🤖</span>
                        查看研报
                      </button>
                      <button
                        onClick={() => handleAddToPortfolio(stock)}
                        className="flex-1 py-1.5 bg-blue-500/10 hover:bg-blue-500/20 border border-blue-500/30 rounded-lg text-blue-400 text-[10px] font-bold transition-all flex items-center justify-center gap-1.5"
                      >
                        <span>📝</span>
                        加入模拟持仓
                      </button>
                    </div>
                  </div>
                </GlassCard>
              ))}
            </div>
          </div>
        )}

        {/* 空状态 */}
        {results.length === 0 && !loading && selectedStrategy && !executingId && (
          <div className="py-20 text-center">
            <div className="text-4xl mb-4">🔍</div>
            <p className="text-white/40 text-sm">该策略当前未筛选到符合条件的股票</p>
          </div>
        )}
      </div>

      {/* 绩效统计弹窗 */}
      {showPerformanceModal && performanceStats && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-in fade-in zoom-in duration-300">
          <GlassCard className="w-full max-w-sm p-6 border-white/20 shadow-2xl relative">
            <button
              onClick={() => setShowPerformanceModal(false)}
              className="absolute top-4 right-4 text-white/40 hover:text-white"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <div className="text-center mb-6">
              <h3 className="text-xl font-bold text-white mb-1">信号绩效报告</h3>
              <p className="text-white/40 text-[10px]">基于过去 30 天实盘信号表现</p>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="bg-white/5 rounded-xl p-3 border border-white/10">
                <div className="text-white/40 text-[10px] mb-1">平均收益 (T+1)</div>
                <div
                  className={`text-lg font-mono font-bold ${performanceStats.avgPnl1d >= 0 ? 'text-red-400' : 'text-green-400'}`}
                >
                  {performanceStats.avgPnl1d >= 0 ? '+' : ''}
                  {performanceStats.avgPnl1d.toFixed(2)}%
                </div>
              </div>
              <div className="bg-white/5 rounded-xl p-3 border border-white/10">
                <div className="text-white/40 text-[10px] mb-1">最大平均浮盈</div>
                <div className="text-lg font-mono font-bold text-amber-400">
                  +{performanceStats.avgMaxPnl.toFixed(2)}%
                </div>
              </div>
            </div>

            <div className="space-y-3 mb-6">
              <div className="text-white/60 text-xs font-bold px-1">按置信度分层表现</div>
              {performanceStats.byScore.map((range: any, idx: number) => (
                <div
                  key={idx}
                  className="bg-white/5 rounded-lg p-2 flex items-center justify-between text-xs border border-white/5"
                >
                  <span className="text-white/80">{range.label.replace('>=', '&gt;=')}</span>
                  <div className="flex items-center gap-3">
                    <span className="text-white/40 font-mono">{range.total}次</span>
                    <span className={`font-bold font-mono ${range.winRate > 60 ? 'text-red-400' : 'text-white/60'}`}>
                      {range.winRate.toFixed(0)}% 胜率
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {performanceStats.synergyStats && (
              <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-3 mb-6">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-blue-400">⚡</span>
                  <span className="text-blue-300 text-xs font-bold">板块共振效应 (Premium)</span>
                </div>
                <div className="flex justify-between items-center text-[10px]">
                  <span className="text-white/60">共振信号胜率:</span>
                  <span className="text-red-400 font-bold">{performanceStats.synergyStats.winRate.toFixed(1)}%</span>
                </div>
                <div className="flex justify-between items-center text-[10px] mt-1">
                  <span className="text-white/60">平均超额收益:</span>
                  <span className="text-white font-mono">{performanceStats.synergyStats.avgPnl.toFixed(2)}%</span>
                </div>
              </div>
            )}

            <button
              onClick={() => setShowPerformanceModal(false)}
              className="w-full py-3 bg-white/10 hover:bg-white/20 text-white rounded-xl font-bold transition-all duration-300 border border-white/10"
            >
              关闭报告
            </button>
          </GlassCard>
        </div>
      )}

      {/* 数据完整性提示弹窗 */}
      {showStatsModal && stats && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-300">
          <GlassCard className="w-full max-w-sm p-6 border-white/20 shadow-2xl">
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="w-16 h-16 bg-yellow-500/20 rounded-full flex items-center justify-center mb-2">
                <svg className="w-8 h-8 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>

              <h3 className="text-xl font-bold text-white">数据完整性提示</h3>

              <div className="w-full space-y-3 py-2">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-white/60">最新交易日期</span>
                  <span className="text-white font-mono">{stats.latestDate || '无数据'}</span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-white/60">活跃股票总数</span>
                  <span className="text-white font-mono">{stats.totalStocks} 只</span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-green-400/80">已同步 K 线</span>
                  <span className="text-green-400 font-mono font-bold">{stats.syncedCount} 只</span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-red-400/80">未同步 K 线</span>
                  <span className="text-red-400 font-mono font-bold">{stats.unsyncedCount} 只</span>
                </div>
              </div>

              <div className="w-full bg-white/5 rounded-lg p-3">
                <p className="text-[11px] text-white/40 leading-relaxed text-left">
                  提示：当前数据库中部分股票的 K 线数据尚未同步完成，可能会影响选股结果的准确性。建议在收盘 1
                  小时后再进行策略运行。
                </p>
              </div>

              <button
                onClick={() => setShowStatsModal(false)}
                className="w-full py-3 bg-blue-600 hover:bg-blue-500 text-white rounded-xl font-bold transition-all duration-300 shadow-lg shadow-blue-900/40"
              >
                我知道了
              </button>
            </div>
          </GlassCard>
        </div>
      )}

      {/* AI 研报弹窗 */}
      {showReportModal && (
        <ResearchReportModal
          report={currentReport}
          loading={isReportLoading}
          onClose={() => {
            setShowReportModal(false);
            setCurrentReport(null);
          }}
        />
      )}
    </GradientBackground>
  );
});

export default StockStrategies;
